import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Settings,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, DataTable } from '../../components/shared';
import PillToggle from '../../components/shared/PillToggle';
import { useAdminTermsOfService } from '../../hooks/useAdminTermsOfService';
import { useToastContext } from '../../contexts/ToastContext';
import CreateTermsOfServiceModal from '../../components/admin/CreateTermsOfServiceModal';
import CreateTermsSettingsModal from '../../components/admin/CreateTermsSettingsModal';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminTermsOfService = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const { success, error: showError } = useToastContext();

  const {
    sections,
    isLoading,
    error,
    settings,
    createSection,
    isCreating,
    updateSection,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteSection,
    updateSettings,
    isUpdatingSettings,
  } = useAdminTermsOfService();

  const handleToggleStatus = (section) => {
    setTogglingId(section.id);
    toggleStatus(section.id, {
      onSuccess: () => {
        success(`Section ${section.is_active ? 'deactivated' : 'activated'} successfully`);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to update section status');
      },
      onSettled: () => setTogglingId(null),
    });
  };

  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (section) => (
        <div className="flex items-center gap-3">
          {section.icon && (
            <div className="flex-shrink-0 w-8 h-8 bg-gray-900 dark:bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white dark:text-gray-900" />
            </div>
          )}
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
            {section.title || 'N/A'}
          </p>
        </div>
      )
    },
    {
      key: 'content',
      label: 'Paragraphs',
      sortable: false,
      width: 'w-24',
      render: (section) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {section.content?.length || 0} paragraph(s)
        </span>
      )
    },
    {
      key: 'sort_order',
      label: 'Order',
      sortable: true,
      width: 'w-24',
      render: (section) => (
        <span className="text-sm text-gray-900 dark:text-gray-50">
          {section.sort_order || 0}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (section) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
              section.is_active
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            {section.is_active ? 'Active' : 'Inactive'}
          </span>
          <PillToggle
            checked={Boolean(section.is_active)}
            onChange={() => handleToggleStatus(section)}
            loading={togglingId === section.id && isToggling}
            disabled={togglingId === section.id && isToggling}
            variant={section.is_active ? 'success' : 'amber'}
            onLabel="On"
            offLabel="Off"
            ariaLabel={`Toggle section: ${section.title}`}
          />
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (section) => {
        setEditingSection(section);
        setShowCreateModal(true);
      },
      variant: 'outline'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (section) => {
        if (window.confirm('Are you sure you want to delete this section?')) {
          deleteSection(section.id, {
            onSuccess: () => {
              success('Section deleted successfully');
            },
            onError: (err) => {
              showError(err.response?.data?.message || 'Failed to delete section');
            }
          });
        }
      },
      variant: 'danger'
    },
  ];

  const handleSave = (data) => {
    if (editingSection) {
      updateSection({
        id: editingSection.id,
        data
      }, {
        onSuccess: () => {
          success('Section updated successfully');
          setShowCreateModal(false);
          setEditingSection(null);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to update section');
        }
      });
    } else {
      createSection(data, {
        onSuccess: () => {
          success('Section created successfully');
          setShowCreateModal(false);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to create section');
        }
      });
    }
  };

  const handleSettingsSave = (data) => {
    updateSettings(data, {
      onSuccess: () => {
        success('Settings updated successfully');
        setShowSettingsModal(false);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to update settings');
      }
    });
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Terms of Service"
        subtitle="Manage terms of service sections and settings."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="!rounded-xl"
              onClick={() => setShowSettingsModal(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              className="!rounded-xl"
              onClick={() => {
                setEditingSection(null);
                setShowCreateModal(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Section
            </Button>
          </div>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.termsOfService.title}
        steps={ADMIN_PAGE_GUIDES.termsOfService.steps}
        icon={ListChecks}
      />

      {settings && (
        <div className="rounded-2xl border border-sky-200 dark:border-sky-800/50 bg-sky-50/70 dark:bg-sky-950/30 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-sky-900 dark:text-sky-200">
              Last Updated: {settings.last_updated || 'Not set'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="!rounded-xl"
              onClick={() => setShowSettingsModal(true)}
            >
              Edit Settings
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading terms sections…</p>
            </div>
          ) : (
            <DataTable
              data={sections}
              columns={columns}
              actions={actions}
              isLoading={false}
              error={error}
              searchPlaceholder="Search sections..."
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      <CreateTermsOfServiceModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingSection(null);
        }}
        onSave={handleSave}
        section={editingSection}
        isEdit={!!editingSection}
        isLoading={isCreating || isUpdating}
      />

      <CreateTermsSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSave={handleSettingsSave}
        settings={settings}
        isLoading={isUpdatingSettings}
      />
    </div>
  );
};

export default AdminTermsOfService;
